import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function calcNoOfDays(fromDate, toDate, hfDay) {
  if (!fromDate || !toDate) return "";
  const from = new Date(fromDate);
  const to   = new Date(toDate);
  if (isNaN(from) || isNaN(to) || from > to) return "";
  const diff = Math.round((to - from) / 86400000) + 1;
  if (hfDay === "W") return String(diff);
  return diff === 1 ? "0.5" : String(diff - 0.5);
}

const getToday = () => new Date().toISOString().slice(0, 10);

export default function NewStudentLeave() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid,   10);
  const empId    = parseInt(localUser?.id,        10);
  const navigate = useNavigate();

  const [students,   setStudents]   = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [availed,    setAvailed]    = useState("");
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");

  const [form, setForm] = useState({
    StudentID:   "",
    LeaveTypeID: "",
    FromDate:    "",
    ToDate:      "",
    NoOfDays:    "",
    HFDay:       "W",
    AppDate:     "",
    Status:      "Approved",
    Remarks:     "",
  });

  useEffect(() => {
    if (moduleId !== 5) return;
    Promise.all([
      fetch(`${API_ROOT}/api/students/leave-students?moduleId=${moduleId}&deptId=${deptId}`).then(r => r.json()),
      fetch(`${API_ROOT}/api/students/leave-types-stu?moduleId=${moduleId}`).then(r => r.json()),
    ])
      .then(([stuData, ltData]) => {
        if (Array.isArray(stuData)) setStudents(stuData);
        if (Array.isArray(ltData))  setLeaveTypes(ltData);
      })
      .catch(() => {});
  }, [moduleId, deptId]);

  useEffect(() => {
    if (!form.StudentID || !form.LeaveTypeID) { setAvailed(""); return; }
    fetch(
      `${API_ROOT}/api/students/leave-availed-stu?moduleId=${moduleId}&studentId=${form.StudentID}&leaveTypeId=${form.LeaveTypeID}`
    )
      .then(r => r.json())
      .then(d => setAvailed(d.success ? String(d.data) : ""))
      .catch(() => setAvailed(""));
  }, [form.StudentID, form.LeaveTypeID, moduleId]);

  useEffect(() => {
    const days = calcNoOfDays(form.FromDate, form.ToDate, form.HFDay);
    setForm(prev => ({ ...prev, NoOfDays: days }));
  }, [form.FromDate, form.ToDate, form.HFDay]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (new Date(form.FromDate) > new Date(form.ToDate)) {
      setError("From Date cannot be after To Date.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_ROOT}/api/students/leave-new`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ...form, moduleId, EntryBy: empId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to add leave.");
      alert(data.message || "Leave successfully added.");
      navigate("/student-leave");
    } catch (err) {
      setError(err.message || "Failed to add leave.");
    } finally {
      setSaving(false);
    }
  };

  if (moduleId !== 5) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">New Leave (by Student Section)</h3>

      <div className="mb-3 d-flex gap-2">
        <button type="button" className="btn btn-success btn-sm" onClick={() => navigate("/student-leave")}>
          View Leaves
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate("/student-ind-leave")}>
          Individual Stu Leave
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={onSubmit}>

        {/* Row 1: Student | Leave Type | Leave Availed */}
        <div className="row g-2 mb-2">
          <div className="col-sm-5">
            <label className="form-label mb-1">Select Student</label>
            <select className="form-select" name="StudentID" value={form.StudentID} onChange={onChange} required>
              <option value="">Select Student</option>
              {students.map(s => (
                <option key={s.StudentID} value={s.StudentID}>
                  {s.StudentName} - {s.RollNo} - {s.CourseAbb}
                </option>
              ))}
            </select>
          </div>

          <div className="col-sm-5">
            <label className="form-label mb-1">Type of Leave</label>
            <select className="form-select" name="LeaveTypeID" value={form.LeaveTypeID} onChange={onChange} required>
              <option value="">Select Leave</option>
              {leaveTypes.map(lt => (
                <option key={lt.LeaveTypeID} value={lt.LeaveTypeID}>{lt.LeaveType}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">Leave Availed</label>
            <input className="form-control" type="text" value={availed} readOnly disabled />
          </div>
        </div>

        {/* Row 2: From Date | To Date | No. of Days | Half/Full Day | Leave App Date | Status */}
        <div className="row g-2 mb-2">
          <div className="col-sm-2">
            <label className="form-label mb-1">From Date</label>
            <input className="form-control" type="date" name="FromDate" value={form.FromDate} onChange={onChange} required />
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">To Date</label>
            <input className="form-control" type="date" name="ToDate" value={form.ToDate} onChange={onChange} required />
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">No. of Days</label>
            <input className="form-control" type="number" name="NoOfDays" value={form.NoOfDays} onChange={onChange} step="0.01" required />
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">Half/Full Day</label>
            <select className="form-select" name="HFDay" value={form.HFDay} onChange={onChange} required>
              <option value="W">Full Day</option>
              <option value="F">First Half</option>
              <option value="S">Second Half</option>
            </select>
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">Leave App Date</label>
            <input className="form-control" type="date" name="AppDate" value={form.AppDate} onChange={onChange} placeholder={getToday()} />
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">Status</label>
            <select className="form-select" name="Status" value={form.Status} onChange={onChange} required>
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
        </div>

        {/* Row 3: Remarks */}
        <div className="row g-2 mb-3">
          <div className="col-12">
            <label className="form-label mb-1">Reason for Leave/Remarks</label>
            <input className="form-control" type="text" name="Remarks" value={form.Remarks} onChange={onChange} maxLength={255} />
          </div>
        </div>

        <div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Add Leave"}
          </button>
          <button type="button" className="btn btn-secondary ms-2" onClick={() => navigate("/student-leave")}>
            Cancel
          </button>
        </div>

      </form>
    </div>
  );
}
